import { motion, useInView, useAnimation } from 'framer-motion';
import { useRef, useEffect } from 'react';
import rightImage from '../../../assets/images/about_screen/about_baby.webp';
import { Link } from 'react-router-dom';

const AboutUsSection = () => {
  const imageRef = useRef(null);
  const isInView = useInView(imageRef, { once: true });
  const animationControl = useAnimation();

  const paragraphStyles = `text-secondary text-lg 2xl:text-2xl`;

  useEffect(() => {
    if (isInView) {
      animationControl.start({
        opacity: 1,
        y: 0,
        transition: { duration: 0.9, ease: 'easeOut' },
      });
    }
  }, [isInView, animationControl]);
  return (
    <>
      <div className="w-full flex flex-col justify-between gap-10 md:gap-24 max-w-[1250px] mx-auto px-4 pt-0 md:px-10 py-8 md:py-20 relative z-10">
        <div className="flex flex-col-reverse md:flex-row sm:gap-10 xl:gap-20">
          <div className="flex-1 flex flex-col justify-center gap-6">
            <div>
              <h2 className="text-4xl lg:text-[52px] 2xl:text-[65px] text-primary font-bold leading-[50px] sm:leading-[75px]">
                About Us
              </h2>
              <h4 className="text-xl lg:text-3xl 2xl:text-4xl text-primary font-bold">
                Guidepost Pediatric Therapies <br /> and Educational Services
              </h4>
            </div>
            <p className={paragraphStyles}>
              Here at Guidepost Pediatrics, we offer{' '}
              <Link
                to="/services/occupational-therapy"
                className="text-primary font-semibold hover:underline"
              >
                Pediatric Occupational Therapy
              </Link>
              ,{' '}
              <Link
                to="/services/speech-therapy"
                className="text-primary font-semibold hover:underline"
              >
                Pediatric Speech Therapy
              </Link>
              ,{' '}
              <Link
                to="/services/physical-therapy"
                className="text-primary font-semibold hover:underline"
              >
                Pediatric Physical Therapy
              </Link>
              , Dyslexia Testing, Early Intervention,{' '}
              <Link
                to="/services/behavioral-therapy"
                className="text-primary font-semibold hover:underline"
              >
                Pediatric Behavioral Therapies
              </Link>
              , and individually tailored Educational Services to meet your
              child’s needs.
            </p>
            <p className={paragraphStyles}>
              Our providers are trained at world-class facilities to treat
              conditions like but not limited to Scoliosis/Spine Disorders, Foot
              Disorders, Hand and Hip Disorders, Concussion Management, and
              Complex Neurological Disorders. We address sensory and emotional
              regulation issues and develop plans of care to tackle the
              challenges patients face. We are a family-oriented close-knit team
              of practitioners ready to serve you.
            </p>
          </div>
          <div className="sm:flex-1">
            <motion.div
              initial={{ opacity: 0, y: -380 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: 'easeOut' }}
              viewport={{ once: true, amount: 0.3 }}
              className="flex justify-center items-center md:hidden"
            >
              <img
                src={rightImage}
                alt="Pediatric therapy sensory swing in Wasilla, Alaska"
                className="min-w-[90vw] max-h-[400px] object-cover sm:object-contain"
              />
            </motion.div>
          </div>
        </div>
        <div className="flex flex-col md:flex-row gap-10 xl:gap-20">
          <div className="flex-1">
            <h4 className="text-3xl text-primary font-bold">Our Mission</h4>
            <div className={`${paragraphStyles} mt-4 flex flex-col gap-1`}>
              <p className="">At Guidepost Pediatric, our aim is to:</p>
              <ul className="list-decimal ml-5">
                <li>
                  Provide children with
                  <span className="font-semibold text-black">
                    &nbsp;customized, engaging, and motivating&nbsp;
                  </span>
                  occupational, speech-language, and physical therapy.
                </li>
                <li>
                  <span className="font-semibold text-black">
                    &nbsp;Empower families&nbsp;
                  </span>
                  through education and compassionate wraparound care.
                </li>
                <li>
                  Build a community of professionals who
                  <span className="font-semibold text-black">
                    &nbsp;care for the "whole child"&nbsp;
                  </span>
                  rather than treat a set of symptoms.
                </li>
              </ul>
            </div>
          </div>
          <div className="flex-1">
            <h4 className="text-3xl text-primary font-bold">Our Modalities</h4>
            <p className={`${paragraphStyles} mt-4`}>
              At Guidepost Pediatric, our skilled therapists utilize a wide
              array of therapeutic modalities to assist in the treatment of your
              child and their specific therapy goals. These include infant
              massage, neuromuscular re-education, kinesiotaping,
              instrument-assisted soft tissue mobilization, use of assistive
              devices and education on orthotics, therapeutic listening, sensory
              integration therapy, neurodevelopmental treatment, home exercise
              program use, sensory-based and play-based intervention,
              articulation therapy, fluency therapy, Augmentative and
              Alternative Communication, and modeling techniques.
            </p>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: -380 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: 'easeOut' }}
          viewport={{ once: true, amount: 0.8 }}
          className="object-contain object-top absolute top-0 lg:-top-[100px] 2xl:-top-[230px] 3xl:-top-[260px] right-0 2xl:-right-[60px] 3xl:-right-[240px] hidden md:block"
        >
          <img
            src={rightImage}
            alt="Pediatric therapy sensory swing in Wasilla, Alaska"
            className="w-full max-w-[450px] lg:max-w-[600px] 2xl:max-w-[760px] 3xl:max-w-[950px]"
          />
        </motion.div>
      </div>
    </>
  );
};

export default AboutUsSection;
